import { EnemyType } from '@/config/GameConfig';

/** Layout types for procedural generation */
export type LayoutType = 'horizontal' | 'vertical' | 'central_base' | 'multi_spawn' | 'spiral' | 'cross';

/** Parameters controlling procedural map generation */
export interface ProceduralMapParams {
  cols: number;
  rows: number;
  pathCount: number;         // 1-6
  layoutType: LayoutType;
  wallDensity: number;       // 0.0-0.4
  placementRatio: number;    // ratio of ground tiles converted to placement
}

/** Difficulty label by tier */
export type DifficultyLabel =
  | 'Beginner' | 'Easy' | 'Normal' | 'Moderate'
  | 'Challenging' | 'Hard' | 'Very Hard' | 'Expert' | 'Nightmare' | 'Infernal';

/** Get difficulty label from tier (0-35) */
export function difficultyForTier(tier: number): DifficultyLabel {
  if (tier <= 3) return 'Beginner';
  if (tier <= 6) return 'Easy';
  if (tier <= 9) return 'Normal';
  if (tier <= 12) return 'Moderate';
  if (tier <= 16) return 'Challenging';
  if (tier <= 20) return 'Hard';
  if (tier <= 25) return 'Very Hard';
  if (tier <= 29) return 'Expert';
  if (tier <= 32) return 'Nightmare';
  return 'Infernal';
}

/** Unlock cost for a given tier (exponential curve) */
export function unlockCost(tier: number, isMerge: boolean): number {
  const base = Math.floor(30 * Math.pow(1.14, tier));
  return isMerge ? Math.floor(base * 1.5) : base;
}

/** Wave count for a given tier */
export function waveCount(tier: number): number {
  return Math.min(5 + Math.floor(tier / 5), 10);
}

/** Starting resources for a given tier */
export function startingResources(tier: number): number {
  return Math.max(40 - tier, 15);
}

/** Enemy reward multiplier (scales down so money is scarce) */
export function rewardMultiplier(tier: number): number {
  return Math.max(0.4, 1.0 - tier * 0.02);
}

/** Enemies allowed at a given tier */
export function enemiesForTier(tier: number): EnemyType[] {
  const enemies: EnemyType[] = [EnemyType.GRUNT];
  if (tier >= 2) enemies.push(EnemyType.RUNNER);
  if (tier >= 5) enemies.push(EnemyType.SWARM);
  if (tier >= 7) enemies.push(EnemyType.TANK);
  if (tier >= 10) enemies.push(EnemyType.PHANTOM);
  if (tier >= 12) enemies.push(EnemyType.DRONE);
  if (tier >= 14) enemies.push(EnemyType.HEALER);
  if (tier >= 15) enemies.push(EnemyType.WASP);
  if (tier >= 16) enemies.push(EnemyType.HIVE);
  if (tier >= 18) enemies.push(EnemyType.BRUTE);
  if (tier >= 22) enemies.push(EnemyType.BOMBER);
  if (tier >= 26) enemies.push(EnemyType.SPECTER);
  return enemies;
}

/** Map size for a given tier (cols, rows) */
export function mapSizeForTier(tier: number): { cols: number; rows: number } {
  // Interpolate from 10x6 to 40x30
  const t = tier / 32;
  const cols = Math.round(10 + t * 30);  // 10 → 40
  const rows = Math.round(6 + t * 24);   // 6 → 30
  return { cols, rows };
}

/** Path count for a given tier */
export function pathCountForTier(tier: number): number {
  if (tier <= 4) return 1;
  if (tier <= 10) return 2;
  if (tier <= 18) return Math.min(3, 1 + Math.floor(tier / 6));
  return Math.min(5, 2 + Math.floor(tier / 8));
}

/** Layout type sequence — cycles through types with some variation */
const LAYOUT_SEQUENCE: LayoutType[] = [
  'horizontal', 'horizontal', 'vertical', 'horizontal',
  'multi_spawn', 'horizontal', 'vertical', 'cross',
  'central_base', 'horizontal', 'spiral', 'vertical',
];

export function layoutForIndex(index: number): LayoutType {
  return LAYOUT_SEQUENCE[index % LAYOUT_SEQUENCE.length];
}

/** Build ProceduralMapParams from tier and seed variation */
export function paramsForTier(tier: number, variation: number): ProceduralMapParams {
  const { cols, rows } = mapSizeForTier(tier);
  return {
    cols,
    rows,
    pathCount: pathCountForTier(tier),
    layoutType: layoutForIndex(tier * 3 + variation),
    wallDensity: Math.min(0.05 + tier * 0.008, 0.35),
    placementRatio: Math.max(0.35 - tier * 0.005, 0.15),
  };
}
